import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {CChecklistPage, CChecklistRow} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {indentPaddingLeft, resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface CChecklistViewProps {
    header: AuditProgramSectionVO;
    page: CChecklistPage;
    refCode: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

/** Native input matching InputField compact outlined chrome — much cheaper than MUI TextField. */
const nativeInputSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CompactInput = memo(function CompactInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeInputSx, textAlign: align}}
        />
    );
});

const DataRow = memo(function DataRow({
    row,
    comments,
    wpRef,
    onChange,
}: {
    row: CChecklistRow;
    comments: string;
    wpRef: string;
    onChange: (key: string, value: string) => void;
}) {
    const pl = indentPaddingLeft(resolveDisplayLevel(row));
    const onComments = useCallback((v: string) => onChange(`${row.id}.comments`, v), [onChange, row.id]);
    const onWpRef = useCallback((v: string) => onChange(`${row.id}.wpRef`, v), [onChange, row.id]);

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={600} sx={{pl}}>
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4, pl}}>
                    {row.text}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={comments} onChange={onComments}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={wpRef} onChange={onWpRef} align="center"/>
            </TableCell>
        </TableRow>
    );
});

/** Generic C-section checklist: Comments / WP Ref (native inputs + memoized rows for large pages e.g. C8.1). */
export default function CChecklistView({header, page, refCode, values, onChange}: CChecklistViewProps) {
    // Parent often passes an inline onChange; keep a stable ref so memoized rows don't all re-render.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode={refCode}
                values={values}
                onChange={stableOnChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            {page.intros.map((intro, idx) => (
                <Typography
                    key={idx}
                    variant="body2"
                    sx={{
                        mb: 1.25,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-line',
                        fontStyle: refCode === 'C2.1' ? 'italic' : 'normal',
                    }}
                >
                    {intro}
                </Typography>
            ))}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 800, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 72}}/>
                        <col/>
                        <col style={{width: 200}}/>
                        <col style={{width: 100}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">WP Ref</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {page.rows.map((row) => {
                            if (row.kind === 'section') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{py: 0.25, whiteSpace: 'pre-line'}}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.kind === 'note') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={3}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    py: 0.25,
                                                    fontStyle: 'italic',
                                                    color: 'text.secondary',
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <CompactInput
                                                value={values[`${row.id}.wpRef`] ?? ''}
                                                onChange={(v) => stableOnChange(`${row.id}.wpRef`, v)}
                                                align="center"
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.kind === 'heading') {
                                const pl = indentPaddingLeft(resolveDisplayLevel(row));
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx}>
                                            <Typography variant="body2" fontWeight={600} sx={{pl}}>
                                                {row.num}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={3}>
                                            <Typography variant="body2" sx={{lineHeight: 1.4, pl, whiteSpace: 'pre-line'}}>
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            return (
                                <DataRow
                                    key={row.id}
                                    row={row}
                                    comments={values[`${row.id}.comments`] ?? ''}
                                    wpRef={values[`${row.id}.wpRef`] ?? ''}
                                    onChange={stableOnChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
